/* eslint-disable @typescript-eslint/no-explicit-any */

/* This is usually imported from @hookform/resolvers/zod, but we need to use our own resolver to avoid
 * having to import the entire @hookform/resolvers package which has issues with newer typescript versions for now.
 * See https://github.com/react-hook-form/resolvers/issues/100
 *
 * This code is from: https://github.com/react-hook-form/resolvers/blob/master/zod/src/zod.ts
 */

import { toNestErrors, validateFieldsNatively } from '@hookform/resolvers';
import { FieldError, FieldValues, Resolver, appendErrors } from 'react-hook-form';
import { ZodError, z } from 'zod';

const isZodError = (error: any): error is ZodError => Array.isArray(error?.errors);

function parseErrorSchema(zodErrors: z.ZodIssue[], validateAllFieldCriteria: boolean) {
  const errors: Record<string, FieldError> = {};
  for (; zodErrors.length; ) {
    const error = zodErrors[0];
    const { code, message, path } = error;
    const _path = path.join('.');

    if (!errors[_path]) {
      if ('unionErrors' in error) {
        const unionError = error.unionErrors[0].errors[0];

        errors[_path] = {
          message: unionError.message,
          type: unionError.code,
        };
      } else {
        errors[_path] = { message, type: code };
      }
    }

    if ('unionErrors' in error) {
      error.unionErrors.forEach(unionError => unionError.errors.forEach(e => zodErrors.push(e)));
    }

    if (validateAllFieldCriteria) {
      const types = errors[_path].types;
      const messages = types && types[error.code];

      errors[_path] = appendErrors(
        _path,
        validateAllFieldCriteria,
        errors,
        code,
        messages ? ([] as string[]).concat(messages as string[], error.message) : error.message,
      ) as FieldError;
    }

    zodErrors.shift();
  }

  return errors;
}

/**
 * Creates a resolver function for react-hook-form that validates form data using a Zod schema
 * @param {z.ZodSchema<TFieldValues>} schema - The Zod schema used to validate the form data
 * @param {Partial<z.ParseParams>} [schemaOptions] - Optional configuration options for Zod parsing
 * @param {Object} [resolverOptions] - Optional resolver-specific configuration
 * @param {('async'|'sync')} [resolverOptions.mode='async'] - Validation mode. Use 'sync' for synchronous validation
 * @param {boolean} [resolverOptions.raw=false] - If true, returns the raw form values instead of the parsed data
 * @returns {Resolver<z.infer<typeof schema>>} A resolver function compatible with react-hook-form
 * @throws {Error} Throws if validation fails with a non-Zod error
 * @example
 * const schema = z.object({
 *   name: z.string().min(2),
 *   age: z.number().min(18)
 * });
 *
 * useForm({
 *   resolver: zodResolver(schema)
 * });
 */
export function zodResolver<TFieldValues extends FieldValues>(
  schema: z.ZodSchema<TFieldValues, any, any>,
  schemaOptions?: Partial<z.ParseParams>,
  resolverOptions: {
    mode?: 'async' | 'sync';
    raw?: boolean;
  } = {},
): Resolver<z.infer<typeof schema>> {
  return (async (values, _, options) => {
    try {
      const data = await schema[resolverOptions.mode === 'sync' ? 'parse' : 'parseAsync'](
        values,
        schemaOptions,
      );

      if (options.shouldUseNativeValidation) validateFieldsNatively({}, options);

      return {
        errors: {},
        values: resolverOptions.raw ? Object.assign({}, values) : data,
      };
    } catch (error: any) {
      if (isZodError(error)) {
        return {
          values: {},
          errors: toNestErrors(
            parseErrorSchema(
              error.errors,
              !options.shouldUseNativeValidation && options.criteriaMode === 'all',
            ),
            options,
          ),
        };
      }

      throw error;
    }
  }) as Resolver<z.infer<typeof schema>>;
}
